import React, { useMemo, useState } from "react";
import {
  ClicksBusinessFunnelResponse,
  ClicksBusinessItem,
  ClicksBusinessSort,
} from "../../api/analyticsAdmin";
import { formatRate } from "../../helpers/searchAnalytics";
import { formatInteger } from "../../helpers/formatNumber";
import SectionBody from "./SectionBody";
import { ClicksMetricHeader } from "./clicksMetricHeaders";
import { SectionState } from "./useAnalyticsStream";

import {
  filterBusinesses,
  rankBusinesses,
  sortUsesMinOpens,
  suggestBusinesses,
} from "../../helpers/clicksBusinessRanking";

const SORTS: { key: ClicksBusinessSort; label: string; description: string }[] = [
  {
    key: "Volume",
    label: "Highest volume",
    description: "Most-opened businesses first — where attention concentrates.",
  },
  {
    key: "OutboundRate",
    label: "Highest outbound rate",
    description:
      "Businesses whose opens most often lead to an outbound click (share of opens with an outbound).",
  },
  {
    key: "ConversionRate",
    label: "Highest conversion rate",
    description:
      "Businesses whose opened users most often go on to transact within the conversion window.",
  },
  {
    key: "ZeroConversion",
    label: "Zero / low conversion",
    description:
      "High-traffic businesses that convert least — surfaces demand not turning into transactions. A ranking, not a filter, so rows may still have a few conversions.",
  },
];

const MIN_OPENS_HINT =
  "Hides businesses with fewer than this many opens, so noisy rates from tiny samples don't dominate the ranking.";

const SUGGESTION_LIMIT = 8;

const PAGE_SIZE = 20;

const businessLabel = (row: ClicksBusinessItem): string =>
  row.displayName ?? row.businessId;

// Sort, the small-sample cut and the lookup stay local: they reshape a payload
// already in hand and never touch the request, so none of them belongs in the
// batch the page assembles.
const ClicksBusinessFunnel: React.FC<{
  section: SectionState<ClicksBusinessFunnelResponse>;
}> = ({ section }) => {
  const [sort, setSort] = useState<ClicksBusinessSort>("Volume");
  const [minOpens, setMinOpens] = useState(3);
  const [lookup, setLookup] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [page, setPage] = useState(1);

  const items = useMemo(
    () => (section.status === "ok" ? section.data.items : []),
    [section]
  );

  const activeSort = SORTS.find((s) => s.key === sort) ?? SORTS[0];
  // Ordered here rather than by the server, so changing either control is
  // instant and cannot fail.
  const rows = useMemo(
    () => rankBusinesses(filterBusinesses(items, lookup), sort, minOpens),
    [items, sort, minOpens, lookup]
  );

  const suggestions = useMemo(
    () => suggestBusinesses(items, lookup, SUGGESTION_LIMIT),
    [items, lookup]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Rows shrink as the stream fills in or a control narrows the list, which can
  // leave the current page past the end.
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const pickSuggestion = (name: string) => {
    setLookup(name);
    setSuggestionsOpen(false);
    setHighlighted(-1);
    setPage(1);
  };

  const handleLookupKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestionsOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setSuggestionsOpen(false);
      setHighlighted(-1);
    }
  };

  return (
    <section className="mb-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="text-lg font-medium">Top businesses</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="search"
              value={lookup}
              onChange={(e) => {
                setLookup(e.target.value);
                setSuggestionsOpen(true);
                setHighlighted(-1);
                setPage(1);
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => setSuggestionsOpen(false)}
              onKeyDown={handleLookupKeyDown}
              placeholder="Find business…"
              role="combobox"
              aria-label="Find business"
              aria-expanded={suggestions.length > 0 && suggestionsOpen}
              aria-controls="clicks-business-suggestions"
              aria-activedescendant={
                highlighted >= 0 ? `clicks-business-suggestion-${highlighted}` : undefined
              }
              className="w-44 p-2 border border-gray-300 rounded-lg text-sm"
            />
            {suggestionsOpen && suggestions.length > 0 && (
              <ul
                id="clicks-business-suggestions"
                role="listbox"
                // Mousedown would blur the input and close the list before the
                // click lands; suppressing it lets onClick pick the suggestion.
                onMouseDown={(e) => e.preventDefault()}
                className="absolute top-full left-0 mt-1 w-64 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-10"
              >
                {suggestions.map((name, index) => (
                  <li
                    key={name}
                    id={`clicks-business-suggestion-${index}`}
                    role="option"
                    aria-selected={index === highlighted}
                    onClick={() => pickSuggestion(name)}
                    onMouseEnter={() => setHighlighted(index)}
                    className={`px-3 py-2 text-sm cursor-pointer truncate ${
                      index === highlighted ? "bg-primary-100 text-primary" : "text-gray-800"
                    }`}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label className="text-sm text-gray-600">
            Sort{" "}
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as ClicksBusinessSort);
                setPage(1);
              }}
              className="p-2 border border-gray-300 rounded-lg text-sm ml-1"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {sortUsesMinOpens(sort) && (
            <label className="text-sm text-gray-600" title={MIN_OPENS_HINT}>
              Min opens{" "}
              <input
                type="number"
                min={1}
                value={minOpens}
                onChange={(e) => {
                  setMinOpens(Math.max(1, Number(e.target.value) || 1));
                  setPage(1);
                }}
                title={MIN_OPENS_HINT}
                className="w-20 p-2 border border-gray-300 rounded-lg text-sm ml-1"
              />
            </label>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-4">{activeSort.description}</p>

      <SectionBody
        section={section}
        isEmpty={() => rows.length === 0}
        emptyMessage={
          lookup.trim() !== ""
            ? `No business matches “${lookup.trim()}” in this range — none with that name had any opens.`
            : "No businesses for this range."
        }
      >
        {() => (
          <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">Business</th>
                <ClicksMetricHeader metric="opens" />
                <ClicksMetricHeader metric="outbounds" />
                <ClicksMetricHeader metric="openers" />
                <ClicksMetricHeader metric="pairs" />
                <ClicksMetricHeader metric="converted" />
                <ClicksMetricHeader metric="openToOutbound" />
                <ClicksMetricHeader metric="openToConversion" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => (
                <tr key={row.businessId} className="border-t border-gray-100">
                  <td className="px-4 py-3">{businessLabel(row)}</td>
                  <td className="px-4 py-3 text-right">{formatInteger(row.opens)}</td>
                  <td className="px-4 py-3 text-right">{formatInteger(row.outbounds)}</td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.uniqueOpeners)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.openedPairs)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatInteger(row.convertedPairs)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.openToOutboundRate)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatRate(row.openToConversionRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>{formatInteger(rows.length)} businesses</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                Page {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
          </>
        )}
      </SectionBody>
    </section>
  );
};

export default ClicksBusinessFunnel;
